import { Box, Button, Flex, Group, Text } from '@mantine/core';
import { useNavigate, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { IconChevronLeft } from '@tabler/icons-react';
import {
  camelCaseOrSnakeCaseToWords,
  fieldsToHide,
} from '../data-capture/merge-review/ReviewHelperFunctions';
import { useDeleteEntities } from '../data-capture/merge-review/useDeleteEntities';
import { UpsertEntityInput, useUpsertEntity } from '../data-capture/merge-review/useUpsertEntities';
import dataCaptureClasses from '../data-capture/DataCapture.module.css';
import { GenericErrorPage } from '../error/GenericErrorPage';
import { EntityForm } from '../UI/dynamicUI/EntityForm';
import PageLoader from '../UI/loader/PageLoader';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { EntityWithFieldMetadata } from 'src/UI/dynamicUI/types';

export default function ViewEntityPage() {
  const [searchParams] = useSearchParams();
  const entityId = searchParams.get('entityId') ?? undefined;
  const entityType = searchParams.get('entityType') as
    | 'Account'
    | 'Asset'
    | 'Liability'
    | 'Policy'
    | undefined;
  const navigate = useNavigate();
  const {
    upsertEntities,
    isLoading: isUpsertingEntity,
    isError: upsertingEntityFailed,
  } = useUpsertEntity();
  const { clientId } = useCurrentClientIfAvailable();

  const {
    deleteEntity,
    isLoading: isDeletingEntities,
    isError: deleteEntitiesFailed,
  } = useDeleteEntities();

  const handleUpdate = async (values: UpsertEntityInput[]) => {
    if (!clientId) {
      return;
    }
    await upsertEntities(clientId, values, true);
    await refetch();
    navigate(-1);
  };

  const { data, isLoading, isError, refetch } = useQuery<EntityWithFieldMetadata>({
    queryKey: ['clients', clientId, entityType, 'entities', entityId],
    enabled: !!entityId && !!entityType,
  });

  const handleDelete = async () => {
    if (!entityId || !data || !clientId) {
      return;
    }
    await deleteEntity(data, clientId);
    navigate(-1);
  };

  if (isLoading || isUpsertingEntity || isDeletingEntities) {
    return <PageLoader absolute={false} />;
  }

  if (isError || upsertingEntityFailed || deleteEntitiesFailed) {
    return <GenericErrorPage error={{ isError, upsertingEntityFailed, deleteEntitiesFailed }} />;
  }

  return data ? (
    <Box>
      <Flex direction={'row'} align={'center'}>
        <IconChevronLeft className={dataCaptureClasses.pointer} onClick={() => navigate(-1)} />
        <Text fw={'600'} size={'lg'} ml={'sm'}>
          {camelCaseOrSnakeCaseToWords(data.entityType)}
        </Text>
      </Flex>

      <Box ml={'xl'} pt={'xl'} maw={320}>
        <EntityForm
          entity={data}
          fieldsToHide={fieldsToHide}
          footer={
            <Group>
              <Button size={'xs'} type={'submit'} mt={'xxl'}>
                Update
              </Button>
              <Button variant={'default'} size={'xs'} mt={'xxl'} onClick={handleDelete}>
                Delete
              </Button>
            </Group>
          }
          filterUntouchedFields={false}
          onSubmit={values => handleUpdate(values)}
        />
      </Box>
    </Box>
  ) : null;
}
